import { Alert, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { Callout } from '@/components/notifications/Callout/Callout';
import { CreateMemoModal } from '@/modules/memos/CreateMemoModal/CreateMemoModal';
import { CreatableMemoPromptType } from '@/modules/memos/memos.constants';
import { COLORS } from '@/styles/tokens/colors';

import { useArtifactStudioContext } from '../../contexts/artifactStudio.context';
import { useArtifactTemplates } from '../../hooks/useArtifactTemplates';
import { ArtifactTemplateCard } from './ArtifactTemplateCard';

/**
 * Shows the diagram and memo templates. A memo template opens the create memo
 * modal, as on the Documents page.
 */
export function ArtifactTemplates() {
  const { householdId } = useArtifactStudioContext();
  const [selectedMemoPromptType, setSelectedMemoPromptType] =
    useState<CreatableMemoPromptType | null>(null);
  const { templates, loading, error, showMemoDocumentsNotice } =
    useArtifactTemplates(setSelectedMemoPromptType);

  return (
    <Stack spacing={1}>
      <Typography variant="body-s" sx={{ color: COLORS.TEXT.SECONDARY }}>
        Choose a template
      </Typography>
      {loading && (
        <Typography variant="body-s" role="status">
          Loading templates…
        </Typography>
      )}
      {error && (
        <Alert severity="info">
          Templates are unavailable. You can still build with your own
          instructions.
        </Alert>
      )}
      {showMemoDocumentsNotice && (
        <Callout severity="info-low">
          Upload documents for this household to create text artifacts, such as
          fiduciary overviews and document comparisons.
        </Callout>
      )}
      <Stack direction="row" gap={2} flexWrap="wrap">
        {templates.map(({ id, ...template }) => (
          <ArtifactTemplateCard key={id} {...template} />
        ))}
      </Stack>
      {selectedMemoPromptType && (
        <CreateMemoModal
          isOpen
          householdId={householdId}
          memoPromptType={selectedMemoPromptType}
          onClose={() => setSelectedMemoPromptType(null)}
        />
      )}
    </Stack>
  );
}
